import React from "react";
import { Box } from "@mui/material";
import { PrimaryTooltip } from "../StyledComponent";
import CustomIcon from "../CustomIcon";
import { useFieldIndicator } from "./useFieldIndicator";

/**
 * Hangs a small status marker off the corner of whatever it wraps.
 *
 * Renders `children` untouched when the org flag is off or no rule matches, so call
 * sites never need a conditional - they just wrap and forget.
 *
 * Give it either a `record` (value and reason are read off the row using the rule
 * set's sourceField/reasonField) or explicit `value`/`reason` for callers that have
 * no row object, such as the Digital Twin header.
 *
 * The tooltip is bound to the marker alone: hovering the wrapped value shows nothing.
 */

const isRightPlacement = (placement) => !String(placement || "").endsWith("-left");
const isTopPlacement = (placement) => !String(placement || "").startsWith("bottom");

const FieldStatusIndicator = ({
    children,
    name,
    indicator,
    record,
    value,
    reason,
    placement,
    size = 16,
    glyphSize,
    // Track reserved beside the children so the marker sits clear of the value rather
    // than over it. Callers anchoring to an icon rather than text pass 0 and let the
    // marker overhang the corner instead, via `offset`.
    gutter,
    offset,
    // Border in the surrounding surface colour, to separate the marker from a busy
    // anchor such as the Digital Twin bus icon. Omitted in table cells.
    ringColor,
    "data-testid": dataTestId = "field-status-indicator",
}) => {
    const { active, marker } = useFieldIndicator({ name, indicator, record, value, reason });

    // Nothing configured, or the feature is off: hand the children straight back so the
    // DOM and the layout are exactly what they were before this component existed.
    if (!active) return children ?? null;

    const corner = placement || marker?.placement || "top-right";
    const toRight = isRightPlacement(corner);
    const toTop = isTopPlacement(corner);

    const reserved = gutter !== undefined ? gutter : size + 6;
    const { x = 0, y = -6 } = offset || {};

    return (
        <Box
            component="span"
            sx={{
                position: "relative",
                display: "inline-flex",
                alignItems: "center",
                minWidth: 0,
                ...(toRight ? { paddingRight: `${reserved}px` } : { paddingLeft: `${reserved}px` }),
            }}
        >
            {children}
            {marker && (
            <PrimaryTooltip
                title={marker.tooltip || ""}
                placement="bottom"
                // PrimaryTooltip styles the tooltip through a descendant selector on the
                // popper class, so a plain sx here loses on specificity and the 250px cap
                // stays - which, combined with nowrap, would spill text outside the dark
                // background. "&&&" outranks it so the tooltip widens to fit one line.
                slotProps={{
                    tooltip: { sx: { "&&&": { maxWidth: "none", whiteSpace: "nowrap" } } },
                }}
            >
                <Box
                    component="span"
                    data-testid={dataTestId}
                    role="img"
                    aria-label={marker.tooltip || undefined}
                    sx={{
                        position: "absolute",
                        ...(toRight ? { right: `${x}px` } : { left: `${x}px` }),
                        ...(toTop ? { top: `${y}px` } : { bottom: `${-y}px` }),
                        width: `${size}px`,
                        height: `${size}px`,
                        borderRadius: `${Math.round(size * 0.3)}px`,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        backgroundColor: marker.background,
                        color: marker.color,
                        ...(ringColor ? { border: `2px solid ${ringColor}` } : {}),
                    }}
                >
                    <CustomIcon
                        name={marker.icon}
                        size={glyphSize || Math.round(size * 0.62)}
                    />
                </Box>
            </PrimaryTooltip>
            )}
        </Box>
    );
};

export default FieldStatusIndicator;
